import { NetworkIntegrityAggregate, NetworkIntegrityAggregateResults, NetworkIntegrityAggregateTableRow } from "../models";
import { JSX, useContext, useEffect, useMemo, useState } from "react";
import { ExcessWarningsDescription, NetworkIntegrityAggregateTable, NetworkIntegrityWidgetInfoFlyout, WidgetIcon, WidgetMenu, WidgetTitleBackgroundColor } from ".";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { EntityTypeEnum, NetworkIntegrityAggregateGroup, NetworkIntegrityAggregateKey, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { Link } from "react-router-dom";
import { getPendoClassNameForAggregateKey } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityPendoHelper";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody } from "./Widget";
import Features from "@app/feature-flags/features";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

interface ExcessWarningRowDescriptor {
  aggregateKey: NetworkIntegrityAggregateKey;
  label: string;
  npiType?: EntityTypeEnum;
}

const LEGACY_ROWS: ExcessWarningRowDescriptor[] = [
  { aggregateKey: NetworkIntegrityAggregateKey.ThreePlusSpecialties, label: "3-5 Specialties" },
  { aggregateKey: NetworkIntegrityAggregateKey.SixPlusSpecialties, label: "6+ Specialties" },
  { aggregateKey: NetworkIntegrityAggregateKey.SixPlusAddresses, label: "6-10 Addresses" },
  { aggregateKey: NetworkIntegrityAggregateKey.ElevenPlusAddresses, label: "11+ Addresses" }
];

const NPI_TYPE_ROWS: ExcessWarningRowDescriptor[] = [
  { aggregateKey: NetworkIntegrityAggregateKey.ThreePlusSpecialties, label: "Type 1: 3-5 Specialties", npiType: EntityTypeEnum.Individual },
  { aggregateKey: NetworkIntegrityAggregateKey.SixPlusSpecialties, label: "Type 1: 6+ Specialties", npiType: EntityTypeEnum.Individual },
  { aggregateKey: NetworkIntegrityAggregateKey.SixPlusAddresses, label: "Type 1: 6-10 Addresses", npiType: EntityTypeEnum.Individual },
  { aggregateKey: NetworkIntegrityAggregateKey.ElevenPlusAddresses, label: "Type 1: 11+ Addresses", npiType: EntityTypeEnum.Individual },
  { aggregateKey: NetworkIntegrityAggregateKey.SixPlusAddresses, label: "Type 2: 6-10 Addresses", npiType: EntityTypeEnum.Facility },
  { aggregateKey: NetworkIntegrityAggregateKey.ElevenPlusAddresses, label: "Type 2: 11+ Addresses", npiType: EntityTypeEnum.Facility }
];

const ExcessWarningsWidget = (): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const ffContext = useContext(FeatureFlagContext);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);

  const [excessWarningsResult, setExcessWarningsResult] = useState<NetworkIntegrityAggregateResults>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const title = 'Excess Warnings';
  const titleIcon = WidgetIcon.Warning;
  const titleBackgroundColor = WidgetTitleBackgroundColor.Yellow;
  const countingUpdatesEnabled = ffContext.enabledFeatureFlags.has(Features.EXCESS_WARNINGS_COUNTING_UPDATES);

  useEffect(() => {
    let cancelled = false;
    setExcessWarningsResult(null);
    async function fetchExcessWarningsResults() {
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const result = await networkIntegrityService.getExcessWarningsResults(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
          if (!cancelled) setExcessWarningsResult(result);
        }
      } finally {
        if (!cancelled) setLoading(false);
      };
    };

    if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
      setLoading(true);
      fetchExcessWarningsResults();
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  const getAggregateGroupFromKey = (key: string): string => {
    switch(key) {
      case NetworkIntegrityAggregateKey.ThreePlusSpecialties:
      case NetworkIntegrityAggregateKey.SixPlusSpecialties:
        return NetworkIntegrityAggregateGroup.ExcessSpecialties;
      case NetworkIntegrityAggregateKey.SixPlusAddresses:
      case NetworkIntegrityAggregateKey.ElevenPlusAddresses:
        return NetworkIntegrityAggregateGroup.ExcessAddresses;
      default:
        return undefined;
    }
  };

  const findAggregate = (descriptor: ExcessWarningRowDescriptor): NetworkIntegrityAggregate | undefined => {
    const candidates = excessWarningsResult.networkIntegrityAggregates.filter(w => w.aggregateKey === descriptor.aggregateKey);

    if (!candidates.some(w => w.entityType != null)) return candidates[0];
    if (descriptor.npiType) return candidates.find(w => w.entityType === descriptor.npiType);

    const termedCounts = candidates.filter(w => w.afterTermedCount != null);
    return {
      ...candidates[0],
      afterTermedCount: termedCounts.length ? termedCounts.reduce((sum, w) => sum + w.afterTermedCount, 0) : candidates[0].afterTermedCount,
      count: candidates.reduce((sum, w) => sum + w.count, 0),
      entityType: undefined
    };
  };

  const generateProviderCellContent = (descriptor: ExcessWarningRowDescriptor, aggregate: NetworkIntegrityAggregate | undefined, afterTermedCount: boolean = false): JSX.Element => {
    const count = afterTermedCount ? aggregate?.afterTermedCount : aggregate?.count;
    if (!count) return <span>0</span>;
    return <Link className={getPendoClassNameForAggregateKey(descriptor.aggregateKey)}
          to={{
            pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/excess-warnings-breakdown/${networkIntegrityContext.providerType}/${encodeURIComponent(getAggregateGroupFromKey(descriptor.aggregateKey))}`,
            state: {
              initialNpiType: descriptor.npiType,
              initialRegionSetIds: networkIntegrityContext.regionSetIds,
              initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
              runConfiguration: networkIntegrityContext.projectRunConfiguration
            }
          }}>
            {count.toLocaleString()}
        </Link>;
  };

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading><WidgetHeader title={title} titleBackgroundColor={titleBackgroundColor} titleIcon={titleIcon} /></WidgetLoading>);

  const isSplit = excessWarningsResult?.networkIntegrityAggregates?.some(a => a.entityType != null) ?? false;
  const rowDescriptors = countingUpdatesEnabled && isSplit ? NPI_TYPE_ROWS : LEGACY_ROWS;

  const rows: NetworkIntegrityAggregateTableRow[] = rowDescriptors.map((descriptor) => {
    const row: NetworkIntegrityAggregateTableRow = { aggregateKey: descriptor.label };

    if (!excessWarningsResult) return row;

    const resultEntry = findAggregate(descriptor);
    return {
      ...row,
      afterTermedCount: generateProviderCellContent(descriptor, resultEntry, true),
      count: generateProviderCellContent(descriptor, resultEntry),
      rawAfterTermedCount: resultEntry?.afterTermedCount ?? undefined,
      rawCount: resultEntry?.count ?? undefined
    };
  });

  return (
    <Widget>
      <WidgetHeader title={title} titleBackgroundColor={titleBackgroundColor} titleIcon={titleIcon}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Excess Warnings" description={<ExcessWarningsDescription/>}/>
          {excessWarningsResult && <Link
            className="qes-button qes-button-secondary"
            id="network-integrity-providers__download-report-link"
            to={{
              pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.AllExcessWarnings}`,
              state: {
                reportRowCount: excessWarningsResult.totalAggregateCount,
                runConfiguration: networkIntegrityContext.projectRunConfiguration,
                showConfidenceLevel: project.showNetworkIntegrityCriticalAlerts ?? false
              }
            }}
            type="button" >
            Create All Report
          </Link>}
          <Link
            className="qes-button qes-button-secondary"
            id="network-integrity-providers__excess-addresses-link"
            to={{
              pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/excess-warnings-breakdown/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.ExcessAddresses}`,
              state: {
                initialProviderType: networkIntegrityContext.providerType,
                initialRegionSetIds: networkIntegrityContext.regionSetIds,
                initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
                runConfiguration: networkIntegrityContext.projectRunConfiguration
              }
            }}
            type="button" >
            View All Addresses
          </Link>
          <Link
            className="qes-button qes-button-secondary"
            id="network-integrity-providers__excess-specialties-link"
            to={{
              pathname: `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/excess-warnings-breakdown/${networkIntegrityContext.providerType}/${NetworkIntegrityAggregateGroup.ExcessSpecialties}`,
              state: {
                initialProviderType: networkIntegrityContext.providerType,
                initialRegionSetIds: networkIntegrityContext.regionSetIds,
                initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
                runConfiguration: networkIntegrityContext.projectRunConfiguration
              }
            }}
            type="button" >
            View All Specialties
          </Link>
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        <NetworkIntegrityAggregateTable aggregateHeader="Alert" aggregateCountHeader="Unique Providers" rows={rows} />
      </WidgetBody>
    </Widget>);
}

export { ExcessWarningsWidget };